import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { asDragSource } from "../../components/dragAndDrop/DraggableComponent";
import Icon from "../../components/interface/Icon";
import ItemCard from "../../components/itemCardView/ItemCardView";
import { TIMELINE_VIEW } from "../../constants/identifiers";
import dateFunctions, { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateSubtractionFunctions from "../../modules/definition/calendar/dateFunctions/dateSubtractionFunctions";
import { immutableRecords, PropTypeShapes } from "../../types";
// import debugUtils from "../../utils/debugUtils";
import { getModifierKeysForEvent } from "../../utils/getModifierKeys";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import layoutFunctions from "./layoutFunctions";
import TimelineItemMarker from "./TimelineItemMarker";
let _draggingStartArrow, _draggingEndArrow;

const clickWasStartArrow = e => hasAncestorOfClass(e.target, "startDateArrow");
const clickWasEndArrow = e => hasAncestorOfClass(e.target, "endDateArrow");

const dragCallbacks = {
  beginDrag: _props => {
    // TODO: Do we want to set isDragging or similar...?
  },
  canDrag: (props, dragSource, e) => {
    if (clickWasStartArrow(e)) {
      // return false;
      _draggingStartArrow = true;
    }
    if (clickWasEndArrow(e)) {
      // return false;
      _draggingEndArrow = true;
    }

    return dragSource.component.canDrag();
  },

  getDragSourceParams: props => {
    return {
      itemId: props.itemId,
      itemType: props.type,
      sourceView: props.viewId,
      sourceViewType: TIMELINE_VIEW
    };
  },

  getDragSourceId: props => {
    return `${props.viewId}-${props.groupId}-${props.itemId}`;
  },

  getDragStartParams(_props, _dragItem) {
    return {
      draggingStartArrow: _draggingStartArrow,
      draggingEndArrow: _draggingEndArrow
    };
  },

  getPreviewElement: props => renderNodePreview(props)
};

const renderNodePreview = nodeProps => {
  if (_draggingStartArrow || _draggingEndArrow) {
    return null;
  }

  const cloneProps = _.clone(nodeProps);
  cloneProps.selected = false;
  cloneProps.onClick = null;
  cloneProps.isDragPreview = true;
  cloneProps.virtualOffsetX = 0;
  cloneProps.virtualOffsetY = 0;
  cloneProps.totalRectLeft = 0;
  cloneProps.totalRectTop = 0;
  cloneProps.totalRectWidth = Math.min(nodeProps.totalRectWidth, 10000);
  cloneProps.totalRectRight = cloneProps.totalRectWidth;
  cloneProps.totalRectBottom = cloneProps.totalRectHeight;
  cloneProps.viewportX = 0;

  return renderNode(cloneProps);
};

const renderNode = (
  useProps,
  onChangeEditing,
  onLabelChange,
  onToggleChildren,
  onItemClick,
  onItemDoubleClick,
  onLeftResizeMouseDown,
  onRightResizeMouseDown
) => {
  let classes = ["timelineItemNode"];
  if (useProps.selected) {
    classes.push("selected");
  }
  if (useProps.expandedChildren) {
    classes.push("expandedChildren");
  }
  if (useProps.hasnewSuperItem || useProps.isMeasuring) {
    classes.push("dropTarget");
  }

  let {
    markerRelativeRect,
    uncertainStartRect,
    uncertainEndRect,
    startRectWithChildren,
    endRectWithChildren
  } = useProps;

  // TODO: Important -- Need to change back to using transform instead of
  // left/top positioning for rendering efficiency when scrolling.
  // Switched to positioning for now because it is required for drag and drop
  // to render properly.
  // Once we render drag/drop better with our own custom item instead of the
  // default one, we can hopefully switch back.

  const nodeStyle = {
    left: `${useProps.totalRectLeft - useProps.virtualOffsetX}px`,
    top: `${useProps.totalRectTop - useProps.virtualOffsetY}px`,
    width: `${useProps.totalRectWidth}px`,
    height: `${useProps.totalRectHeight}px`,
    opacity: useProps.isMeasuring
      ? 1
      : useProps.isDragging
        ? _draggingStartArrow || _draggingEndArrow
          ? 0.4
          : 0
        : useProps.isFiltered
          ? 0.4
          : 1
  };

  // Adjust the marker width if we're showing the preview for dragging start/end date
  if ((_draggingStartArrow || _draggingEndArrow) && useProps.dropDate && useProps.canDrop) {
    let obj = { ...useProps };
    if (_draggingStartArrow) {
      if (obj.startDate.timestamp === obj.preferredStartDate.timestamp) {
        obj.startDate = useProps.dropDate;
      }
      obj.preferredStartDate = useProps.dropDate;

      if (isDateValid(obj.earliestEndDate) && obj.earliestEndDate.timestamp < useProps.dropDate.timestamp) {
        obj.earliestEndDate = useProps.dropDate;
      }
      if (isDateValid(obj.latestStartDate) && obj.latestStartDate.timestamp < useProps.dropDate.timestamp) {
        obj.latestStartDate = useProps.dropDate;
      }
    }
    if (_draggingEndArrow) {
      const duration = dateSubtractionFunctions
        .timespanBetweenDates(useProps.calendar, obj.preferredEndDate, useProps.dropDate)
        .toJS();

      if (obj.endDate.timestamp === obj.drawPreferredEndDate.timestamp) {
        obj.endDate = useProps.dropDate;
      }
      obj.drawPreferredEndDate = useProps.dropDate;
      obj.preferredDuration = duration;
      obj.duration = duration;

      if (isDateValid(obj.latestStartDate) && obj.latestStartDate.timestamp > useProps.dropDate.timestamp) {
        obj.latestStartDate = useProps.dropDate;
      }
      if (isDateValid(obj.earliestEndDate) && obj.earliestEndDate.timestamp > useProps.dropDate.timestamp) {
        obj.earliestEndDate = useProps.dropDate;
      }
    }

    const dateForNow = dateFunctions.dateForNow(useProps.calendar);
    let dragPosition = layoutFunctions.calcEventInternalLayout(
      useProps.viewId,
      obj,
      useProps.unitScale,
      useProps.fontSize,
      dateForNow,
      false,
      useProps.showItemCard
    );

    const leftDiff = useProps.totalRectLeft - dragPosition.totalRect.left;

    dragPosition = dragPosition
      .setIn(["markerRelativeRect", "left"], dragPosition.markerRelativeRect.left - leftDiff)
      .setIn(["uncertainStartRect", "left"], dragPosition.uncertainStartRect.left - leftDiff)
      .setIn(["uncertainEndRect", "left"], dragPosition.uncertainEndRect.left - leftDiff)
      .setIn(["startRectWithChildren", "left"], dragPosition.startRectWithChildren.left - leftDiff)
      .setIn(["endRectWithChildren", "left"], dragPosition.endRectWithChildren.left - leftDiff);

    markerRelativeRect = dragPosition.markerRelativeRect;
    uncertainStartRect = dragPosition.uncertainStartRect;
    uncertainEndRect = dragPosition.uncertainEndRect;
    startRectWithChildren = dragPosition.startRectWithChildren;
    endRectWithChildren = dragPosition.endRectWithChildren;
  }

  let left = useProps.totalRectLeft - useProps.virtualOffsetX;
  let width = useProps.totalRectWidth;
  let leftMin = -10000;

  let leftAdjustment = 0;
  if (left < leftMin) {
    leftAdjustment = left - leftMin;
    width = width + leftAdjustment;
    left = leftMin;
    nodeStyle.width = `${width}px`;
    nodeStyle.left = `${left}px`;
  }
  if (width > useProps.canvasRange) {
    width = useProps.canvasRange;
    nodeStyle.width = `${width}px`;
  }

  const arrowStyle = {
    left: `${useProps.childrenArrowRect.left + leftAdjustment}px`,
    top: `${useProps.childrenArrowRect.top}px`,
    width: `${useProps.childrenArrowRect.width}px`,
    height: `${useProps.childrenArrowRect.height}px`
  };

  return (
    <div className={classes.join(" ")} onClick={onItemClick} onDoubleClick={onItemDoubleClick} style={nodeStyle}>
      <TimelineItemMarker
        totalRectLeft={useProps.totalRectLeft}
        rect={markerRelativeRect}
        color={useProps.color}
        label={useProps.label}
        showLabel={!useProps.timelineViewSettings.showItemCard}
        hasChildren={useProps.hasChildren}
        fontSize={useProps.fontSize}
        uncertainStartRect={uncertainStartRect}
        uncertainEndRect={uncertainEndRect}
        ongoing={useProps.ongoing}
        startRectWithChildren={startRectWithChildren}
        endRectWithChildren={endRectWithChildren}
        parentShadowMarkerRect={useProps.parentShadowMarkerRect}
        isDragPreview={useProps.isDragPreview}
        virtualOffsetX={useProps.virtualOffsetX}
        viewportWidth={useProps.viewportWidth}
        viewportX={useProps.viewportX}
        canvasRange={useProps.canvasRange}
        leftAdjustment={leftAdjustment}
        onLeftResizeMouseDown={onLeftResizeMouseDown}
        onRightResizeMouseDown={onRightResizeMouseDown}
        allowDragEnd={useProps.allowDragEnd}
        draggingStartArrow={_draggingStartArrow}
        draggingEndArrow={_draggingEndArrow}
        showItemCard={useProps.showItemCard}
      />
      {useProps.timelineViewSettings.showItemCard ? (
        <ItemCard
          {...useProps.timelineViewSettings.toObject()}
          rect={useProps.eventCardRelativeRect}
          label={useProps.label}
          titleMetrics={useProps.titleMetrics}
          dateTextSize={useProps.dateTextSize}
          narrativeTextSize={useProps.narrativeTextSize}
          durationTextSize={useProps.durationTextSize}
          summaryMetrics={useProps.summaryMetrics}
          identifierSize={useProps.identifierSize}
          identifier={useProps.displayId}
          relatedItemsByType={useProps.relatedItemsByType}
          blockingIdentifiers={useProps.blockingIdentifiers}
          blockedByIdentifiers={useProps.blockedByIdentifiers}
          imagePath={useProps.imagePath}
          isCompleted={useProps.isCompleted}
          onLabelChange={onLabelChange}
          onChangeEditing={onChangeEditing}
          isActiveView={useProps.isActiveView}
          isSelected={useProps.selected}
          autoEditWhenBlank={useProps.autoEditWhenBlank}
          hasViolatedConstraints={useProps.hasViolatedConstraints}
          cardIsOnNewLine={useProps.cardIsOnNewLine}
          typesById={useProps.typesById}
          type={useProps.type}
        />
      ) : null}
      {useProps.hasChildren ? (
        <div className="timelineEventChildrenArrow" style={arrowStyle} onClick={onToggleChildren}>
          <Icon icon={useProps.expandedChildren ? "upArrow" : "downArrow"} color="iconMono" />
        </div>
      ) : null}
    </div>
  );
};

class TimelineItemNode extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.onChangeEditing = this.onChangeEditing.bind(this);
    this.onLabelChange = this.onLabelChange.bind(this);
    this.onToggleChildren = this.onToggleChildren.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.onItemDoubleClick = this.onItemDoubleClick.bind(this);

    this.onResizeMouseDown = this.onResizeMouseDown.bind(this);
    this.onResizeMouseUp = this.onResizeMouseUp.bind(this);
    this.onLeftResizeMouseDown = this.onLeftResizeMouseDown.bind(this);
    this.onRightResizeMouseDown = this.onRightResizeMouseDown.bind(this);

    this._isEditing = false;
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("TimelineItemNode", this.props, nextProps, false, false);
  // }

  canDrag() {
    // Disable the drag and drop when editing inside the node
    // The drag and drop may impact on the click event and focusing.
    return !this._isEditing && this.props.allowDrag;
  }

  onChangeEditing(value) {
    this._isEditing = value;
  }

  onLabelChange(value) {
    this.props.onLabelChange([this.props.itemId], value);
  }

  onToggleChildren() {
    if (this.props.onToggleChildren) {
      this.props.onToggleChildren(this.props.itemId, !this.props.expandedChildren);
    }
  }

  onItemClick(e) {
    const clickWasArrow = hasAncestorOfClass(e.target, "timelineEventChildrenArrow");
    if (!clickWasArrow && this.props.onClick) {
      this.props.onClick(this.props.itemId, getModifierKeysForEvent(e), this.props.selected);
    }
  }

  onItemDoubleClick(_e) {
    if (this.props.onShowInspector) {
      this.props.onShowInspector();
    }
  }

  onLeftResizeMouseDown(e) {
    _draggingStartArrow = true;
    this.onResizeMouseDown(e);
  }

  onRightResizeMouseDown(e) {
    _draggingEndArrow = true;
    this.onResizeMouseDown(e);
  }

  onResizeMouseDown(_e) {
    document.addEventListener("mouseup", this.onResizeMouseUp);
    document.addEventListener("touchend", this.onResizeMouseUp);
  }

  onResizeMouseUp(_e) {
    _draggingStartArrow = false;
    _draggingEndArrow = false;
  }

  render() {
    return renderNode(
      this.props,
      this.onChangeEditing,
      this.onLabelChange,
      this.onToggleChildren,
      this.onItemClick,
      this.onItemDoubleClick,
      this.onLeftResizeMouseDown,
      this.onRightResizeMouseDown
    );
  }
}

TimelineItemNode.propTypes = {
  onClick: PropTypes.func,
  onToggleChildren: PropTypes.func,
  onLabelChange: PropTypes.func,
  selected: PropTypes.bool.isRequired,
  itemId: PropTypes.string.isRequired,
  viewId: PropTypes.string.isRequired,
  groupId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  super: PropTypes.string,
  type: PropTypes.string.isRequired,
  hasChildren: PropTypes.bool,
  expandedChildren: PropTypes.bool,
  label: PropTypes.string,
  titleMetrics: PropTypeShapes.textMetrics.isRequired,
  dateTextSize: PropTypeShapes.textSize.isRequired,
  durationTextSize: PropTypeShapes.textSize.isRequired,
  narrativeTextSize: PropTypeShapes.textSize.isRequired,
  summaryMetrics: PropTypeShapes.textMetrics.isRequired,
  identifierSize: PropTypeShapes.size.isRequired,
  relatedItemsByType: ImmutablePropTypes.list.isRequired,
  blockingIdentifiers: PropTypeShapes.identifierList.isRequired,
  blockedByIdentifiers: PropTypeShapes.identifierList.isRequired,
  imagePath: PropTypes.string,

  // Instead of receiving a position, we want it broken out into its
  // component pieces, since most of those will not change!
  // position: PropTypeShapes.timelineViewEventPosition.isRequired,
  markerRelativeRect: PropTypeShapes.flatRect.isRequired,
  eventCardRelativeRect: PropTypeShapes.flatRect.isRequired,
  childrenArrowRect: PropTypeShapes.flatRect.isRequired,
  childrenBorderRect: PropTypeShapes.flatRect.isRequired,
  parentShadowMarkerRect: PropTypeShapes.flatRect.isRequired,
  uncertainStartRect: PropTypeShapes.flatRect.isRequired,
  uncertainEndRect: PropTypeShapes.flatRect.isRequired,
  startRectWithChildren: PropTypeShapes.flatRect.isRequired,
  endRectWithChildren: PropTypeShapes.flatRect.isRequired,
  cardIsOnNewLine: PropTypes.bool.isRequired,
  // And further, since totalRect is likely to be recalculated
  // in layout but remain the same, we will decompose it further
  // totalRect: PropTypeShapes.flatRect.isRequired,
  totalRectWidth: PropTypes.number.isRequired,
  totalRectHeight: PropTypes.number.isRequired,
  totalRectLeft: PropTypes.number.isRequired,
  totalRectRight: PropTypes.number.isRequired,
  totalRectTop: PropTypes.number.isRequired,
  totalRectBottom: PropTypes.number.isRequired,

  // And the rest below here is as it was
  virtualOffsetX: PropTypes.number.isRequired,
  virtualOffsetY: PropTypes.number.isRequired,
  viewportWidth: PropTypes.number.isRequired,
  viewportX: PropTypes.number.isRequired,
  canvasRange: PropTypes.number.isRequired,
  fontSize: PropTypes.number.isRequired,
  timelineViewSettings: PropTypes.instanceOf(immutableRecords.TimelineViewSettingsRecord).isRequired,
  allowDrag: PropTypes.bool.isRequired,
  isDragPreview: PropTypes.bool.isRequired,
  onShowInspector: PropTypes.func,

  typesById: ImmutablePropTypes.map.isRequired,

  // Should the node automatically edit?
  autoEditWhenBlank: PropTypes.bool.isRequired,

  calendar: PropTypeShapes.calendar.isRequired,
  allowDragEnd: PropTypes.bool.isRequired,
  dropDate: PropTypeShapes.date,
  unitScale: PropTypes.number,
  canDrop: PropTypes.bool,
  showItemCard: PropTypes.bool.isRequired
};

export default asDragSource(TimelineItemNode, TIMELINE_VIEW, dragCallbacks);
